import { tracked } from '@glimmer/tracking';
import Service, { inject as service } from '@ember/service';

export default class PrescreenerGlobalMonarchChannelService extends Service {
  @service session;
  @service store;

  @tracked channel;

  get reservation() {
    return this.store.peekAll('reservation').find(r => !r.isDeleted);
  }

  get reservationGlobalMonarchChannel() {
    let reservation = this.reservation;
    return reservation?.get?.('channel') ?? reservation?.channel ?? null;
  }

  get currentChannelId() {
    return this.channel?.id ?? this.reservationGlobalMonarchChannel?.id ?? null;
  }

  get hasPrescreenerQuestions() {
    return !!this.channel?.hasPrescreenerQuestions;
  }

  async load() {
    this.channel = await this.resolve(this.channel);
    return this.channel;
  }

  async resolve(currentChannel = null) {
    if (this.session.isClientPortalMonarchOrSpWebsite) return null;

    if (currentChannel) return currentChannel;

    let { channelName, baseName } = this.#getRequestedChannelNames();
    if (channelName) {
      let preloaded = this.#resolveFromStore({ channelName, baseName });
      if (preloaded) return preloaded;

      let apiChannel = await this.#resolveFromApi({ channelName, baseName });
      if (apiChannel) return apiChannel;
    }

    let reservationChannel = this.#resolveFromReservation();
    if (reservationChannel) return reservationChannel;

    return null;
  }

  #resolveFromReservation() {
    let reservationChannel = this.reservationGlobalMonarchChannel;
    return reservationChannel?.id ? reservationChannel : null;
  }

  #getRequestedChannelNames() {
    let channelName = this.session.channel;
    if (!channelName) return { channelName: null, baseName: null };

    let baseName = String(channelName).split('_')[0];
    return { channelName, baseName };
  }

  #resolveFromStore({ channelName, baseName }) {
    let preloaded = this.store.peekAll('global-monarch-channel');
    let found = preloaded.findBy('name', channelName) ?? preloaded.findBy('name', baseName) ?? null;
    return found?.id ? found : null;
  }

  async #resolveFromApi({ channelName, baseName }) {
    try {
      let found = await this.#queryByChannelName(channelName);
      if (found || baseName === channelName) return found;

      found = await this.#queryByChannelName(baseName);
      if (found) return found;

      return await this.#queryBySubchannelName(channelName);
    } catch (_e) {
      return null;
    }
  }

  async #queryByChannelName(channelName) {
    return await this.#queryChannel({ channelName });
  }

  async #queryBySubchannelName(subchannelName) {
    return await this.#queryChannel({ subchannelName });
  }

  async #queryChannel(filter) {
    let channels = await this.store.query('global-monarch-channel', { filter });
    return channels?.firstObject ?? null;
  }
}
